import type { CountdownApiClient } from '@countdown/api-client';
import { type ReactElement, useCallback, useState } from 'react';

import { ParticipantPreview } from '@/components/ParticipantPreview';
import { Button } from '@/components/ui/button';
import {
  LOAD_FAILED,
  type PreviewTestData,
  loadTestData,
  purgeTestData,
  useResource,
} from '@/lib/api';

/**
 * The Türchenplan's view "Kalender" (D48, before a tab of its own): the whole calendar of this
 * action as a visitor gets it, from the draft on.
 *
 * WHY THIS SCREEN EXISTS. The door page answers "may this contribution go live" (9.7.5). The
 * week before the season starts the question is what the calendar looks like as a whole, the
 * unsold doors with their fallback included. Every door opens here as on its own day, so
 * walking the calendar is clicking through it. In the running action every door can also be
 * played through as a test run (D21), and whoever holds "privacy" removes what the tests left.
 *
 * Props rather than the action's context: the Türchenplan renders this view, and reading the
 * context here would import the page that imports this one.
 */
export function CalendarPreviewPage({
  actionId,
  title,
  privacy,
}: {
  readonly actionId: string;
  readonly title: string;
  readonly privacy: boolean;
}): ReactElement {
  return (
    <>
      <ParticipantPreview actionId={actionId} title={`Vorschau ${title}`} />
      {privacy ? <TestData actionId={actionId} /> : null}
    </>
  );
}

/**
 * "Testdaten entfernen (N)": the rows the test runs of this action left behind (D21).
 *
 * Tenant admins only, like the participant records, because the removal erases the test
 * participants (D19). N counts rows, test participants and test answers. One click is enough:
 * nothing real is in reach, a real participant behind the same address is a row of its own.
 */
function TestData({ actionId }: { readonly actionId: string }): ReactElement | null {
  const load = useCallback(
    (api: CountdownApiClient) => loadTestData(api, actionId),
    [actionId],
  );
  const counts = useResource<PreviewTestData>(load);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  if (counts.status === 'error') {
    return <p className="mt-6 text-sm text-muted-foreground">{LOAD_FAILED}</p>;
  }
  if (counts.status !== 'ready') {
    return null;
  }

  const { participants, drafts } = counts.data;
  const purge = (): void => {
    setBusy(true);
    setMessage(null);
    purgeTestData(actionId)
      .then((outcome) => {
        setMessage(outcome.ok ? 'Die Testdaten sind entfernt.' : outcome.message);
        counts.reload();
      })
      .catch((error: unknown) => {
        console.error('[CalendarPreviewPage] purge failed', error);
        setMessage(LOAD_FAILED);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <section className="mt-6 max-w-2xl border border-border p-4">
      <h2 className="text-lg font-bold">Testdaten</h2>
      <p className="mt-2 text-sm">
        Probeläufe aus der Vorschau: {participants} Testteilnehmer, {drafts} Testantworten. Sie
        zählen nicht für Verlosung, Statistik und Leads und gehören vor dem ersten Türchen
        entfernt.
      </p>
      <Button
        className="mt-3"
        variant="destructive"
        disabled={busy || counts.pending || participants + drafts === 0}
        onClick={purge}
      >
        Testdaten entfernen ({participants + drafts})
      </Button>
      {message !== null ? (
        <p className="mt-2 text-sm" role="status" aria-live="polite">
          {message}
        </p>
      ) : null}
    </section>
  );
}
